import { useCallback, useMemo, useState } from "react";

import { useShallow } from "zustand/react/shallow";

import { useGetInboundingListClaimersQuery } from "../queries/collaborativeInbound/collaborativeInbound.generated";
import { useInboundStore } from "../stores/inboundStore/useInboundStore";

type SharedListClaimersVariables = {
  listId: string;
  shouldSkip?: boolean;
  onClaimersChange?: () => void;
};

const ERROR_ALLOWANCE_LIMIT = 4;
const FETCH_INTERVAL = 5_000;

export const useSharedListClaimers = ({
  listId,
  shouldSkip,
  onClaimersChange,
}: SharedListClaimersVariables) => {
  const [isPollingStopped, setIsPollingStopped] = useState<boolean>(false);
  const [errorCount, setErrorCount] = useState(0);
  const { setInboundUIState } = useInboundStore(
    useShallow((state) => ({
      setInboundUIState: state.setInboundUIState,
    })),
  );
  const { data, stopPolling, error, loading, refetch, startPolling } =
    useGetInboundingListClaimersQuery({
      skip: shouldSkip,
      variables: { input: { listId } },
      defaultOptions: {
        fetchPolicy: "network-only",
      },
      notifyOnNetworkStatusChange: true,
      pollInterval: FETCH_INTERVAL,
      onCompleted: (response) => {
        if (errorCount) {
          setErrorCount(0);
        }
        if ((data?.getInboundingListClaimers?.claimerIds?.length ?? 0) > 1) {
          onClaimersChange?.();
          stopPolling();
          setIsPollingStopped(true);
        } else {
          setIsPollingStopped(false);
        }
        if (response?.getInboundingListClaimers?.claimerIds === null) {
          setInboundUIState({ isClaimedListNotFoundWarningModalVisible: false });
        }
      },
      onError: ({ networkError, graphQLErrors }) => {
        if (errorCount >= ERROR_ALLOWANCE_LIMIT && (networkError || graphQLErrors)) {
          stopPolling();
          setIsPollingStopped(true);
        }
        setErrorCount((prevCount) => prevCount + 1);
      },
    });

  const claimerIds = useMemo(() => {
    return data?.getInboundingListClaimers?.claimerIds || [];
  }, [data?.getInboundingListClaimers?.claimerIds]);

  const refreshSharedListClaimers = useCallback(() => {
    refetch().then(() => {
      startPolling(FETCH_INTERVAL);
    });
  }, [startPolling, refetch]);

  return {
    claimerIds,
    isPollingStopped,
    error,
    loading,
    refreshSharedListClaimers,
  };
};
